import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Country (ISO-2) -> currency code. ZA always stays in ZAR (base).
const COUNTRY_TO_CCY: Record<string, string> = {
  ZA: "ZAR",
  US: "USD", CA: "USD", MX: "USD",
  GB: "GBP",
  AU: "AUD", NZ: "AUD",
  IN: "INR",
  NG: "NGN", KE: "KES", GH: "GHS", ZW: "USD",
  AE: "AED", SA: "SAR",
  JP: "JPY", CN: "CNY", HK: "HKD", SG: "SGD",
  BR: "BRL",
};
// Eurozone
["DE","FR","IT","ES","PT","NL","BE","IE","AT","FI","GR","LU"].forEach(c => (COUNTRY_TO_CCY[c] = "EUR"));

const SYMBOL: Record<string, string> = {
  ZAR: "R", USD: "$", EUR: "€", GBP: "£", AUD: "A$", INR: "₹",
  NGN: "₦", KES: "KSh", GHS: "GH₵", AED: "د.إ", SAR: "﷼",
  JPY: "¥", CNY: "¥", HKD: "HK$", SGD: "S$", BRL: "R$",
};

type CurrencyState = {
  code: string;        // e.g. "USD"
  symbol: string;
  rate: number;        // 1 ZAR = X target currency
  ready: boolean;
};

const Ctx = createContext<CurrencyState>({ code: "ZAR", symbol: "R", rate: 1, ready: true });

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CurrencyState>({ code: "ZAR", symbol: "R", rate: 1, ready: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const cached = sessionStorage.getItem("brandin_ccy");
        if (cached) {
          const parsed = JSON.parse(cached) as CurrencyState;
          if (alive) setState({ ...parsed, ready: true });
          return;
        }

        // 1) Detect country (free, no key)
        let country = "ZA";
        try {
          const geo = await fetch("https://ipapi.co/json/").then(r => r.ok ? r.json() : null);
          if (geo?.country_code) country = String(geo.country_code).toUpperCase();
        } catch { /* keep ZA */ }

        const code = COUNTRY_TO_CCY[country] ?? "USD";
        if (code === "ZAR") {
          const s = { code: "ZAR", symbol: "R", rate: 1, ready: true };
          sessionStorage.setItem("brandin_ccy", JSON.stringify(s));
          if (alive) setState(s);
          return;
        }

        // 2) Live ZAR->target rate (free, no key)
        let rate = 1;
        try {
          const r = await fetch("https://open.er-api.com/v6/latest/ZAR").then(r => r.ok ? r.json() : null);
          const v = r?.rates?.[code];
          if (typeof v === "number" && v > 0) rate = v;
          else throw new Error("no rate");
        } catch {
          // If rates fail, stay in ZAR rather than show wrong numbers
          const s = { code: "ZAR", symbol: "R", rate: 1, ready: true };
          if (alive) setState(s);
          return;
        }

        const s: CurrencyState = { code, symbol: SYMBOL[code] ?? code + " ", rate, ready: true };
        sessionStorage.setItem("brandin_ccy", JSON.stringify(s));
        if (alive) setState(s);
      } catch {
        if (alive) setState({ code: "ZAR", symbol: "R", rate: 1, ready: true });
      }
    })();
    return () => { alive = false; };
  }, []);

  return <Ctx.Provider value={state}>{children}</Ctx.Provider>;
}

export function useCurrency() {
  return useContext(Ctx);
}

/** Convert a ZAR amount to the visitor's currency and format with rounded "marketing" numbers. */
export function formatPrice(amountZar: number, ccy: CurrencyState): string {
  const converted = amountZar * ccy.rate;
  // Choose rounding granularity by magnitude
  let rounded: number;
  if (converted >= 1000) rounded = Math.round(converted / 50) * 50;       // nearest 50
  else if (converted >= 100) rounded = Math.round(converted / 10) * 10;   // nearest 10
  else rounded = Math.round(converted);
  // No decimals for JPY-style currencies
  const noDecimals = ["JPY", "ZAR", "INR", "NGN", "KES"];
  try {
    const fmt = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: ccy.code,
      maximumFractionDigits: noDecimals.includes(ccy.code) ? 0 : 0,
      minimumFractionDigits: 0,
    });
    return fmt.format(rounded);
  } catch {
    return `${ccy.symbol}${rounded.toLocaleString("en-US")}`;
  }
}
